import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import Sidebar from '../components/Sidebar';

/* ─── helpers ─────────────────────────────────────────── */
const CATEGORIES = [
  { key: 'all',         label: 'All',         icon: '📋' },
  { key: 'leads',       label: 'Leads',        icon: '🎯' },
  { key: 'deals',       label: 'Deals',        icon: '🏠' },
  { key: 'commission',  label: 'Commission',   icon: '💰' },
  { key: 'clients',     label: 'Clients',      icon: '👤' },
  { key: 'introducers', label: 'Introducers',  icon: '🤝' },
  { key: 'team',        label: 'Team',         icon: '👥' },
  { key: 'partners',    label: 'Partners',     icon: '🔗' },
];

const RECIPIENT_COLORS = {
  broker:      { bg: '#ede9fe', color: '#6d28d9' },
  introducer:  { bg: '#d1fae5', color: '#065f46' },
  client:      { bg: '#dbeafe', color: '#1e40af' },
  team_member: { bg: '#fef3c7', color: '#92400e' },
  partner:     { bg: '#fce7f3', color: '#9d174d' },
  admin:       { bg: '#f1f5f9', color: '#475569' },
};

function recipientBadge(type) {
  const style = RECIPIENT_COLORS[type] || RECIPIENT_COLORS.admin;
  const label = type?.replace(/_/g, ' ') || 'broker';
  return (
    <span style={{
      ...style, fontSize: 10, fontWeight: 700, borderRadius: 20,
      padding: '2px 8px', textTransform: 'capitalize', whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}


/* ─── tag insertion helper ─────────────────────────────── */
function insertAtCursor(textareaRef, tag, value, setValue) {
  const el = textareaRef.current;
  if (!el) { setValue(v => v + tag); return; }
  const start = el.selectionStart;
  const end   = el.selectionEnd;
  const next  = value.slice(0, start) + tag + value.slice(end);
  setValue(next);
  requestAnimationFrame(() => {
    el.selectionStart = el.selectionEnd = start + tag.length;
    el.focus();
  });
}

/* ─── email preview ─────────────────────────────────────── */
function EmailPreview({ branding, subject, bodyIntro, bodyExtra, ctaLabel }) {
  const logoUrl   = branding?.logo_url;
  const company   = branding?.company_name || 'Your Company';
  const primary   = branding?.primary_color || '#1e293b';
  const accent    = branding?.accent_color  || '#10b981';

  const fakeBody = (bodyIntro || 'Your email body will appear here.')
    .replace(/\{\{[\w_]+\}\}/g, m => `<span style="background:#fef9c3;color:#92400e;padding:0 3px;border-radius:3px">${m}</span>`);
  const fakeExtra = (bodyExtra || '')
    .replace(/\{\{[\w_]+\}\}/g, m => `<span style="background:#fef9c3;color:#92400e;padding:0 3px;border-radius:3px">${m}</span>`);

  return (
    <div style={{ fontFamily: 'sans-serif', maxWidth: 560, margin: '0 auto', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', fontSize: 14 }}>
      {/* header */}
      <div style={{ background: primary, padding: '20px 32px', display: 'flex', alignItems: 'center', gap: 12 }}>
        {logoUrl
          ? <img src={logoUrl} alt={company} style={{ height: 36, objectFit: 'contain' }} />
          : <div style={{ width: 36, height: 36, borderRadius: '50%', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: primary, fontWeight: 800, fontSize: 16 }}>{company[0]}</div>
        }
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{company}</span>
      </div>
      {/* body */}
      <div style={{ padding: '28px 32px', background: '#fff' }}>
        <p style={{ margin: '0 0 14px', color: '#1e293b', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: fakeBody }} />
        {fakeExtra && <p style={{ margin: '0 0 14px', color: '#475569', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: fakeExtra }} />}
        {ctaLabel && (
          <div style={{ textAlign: 'center', margin: '24px 0 8px' }}>
            <a href="#preview" style={{
              background: accent, color: primary, fontWeight: 700, fontSize: 14,
              borderRadius: 6, padding: '12px 28px', textDecoration: 'none', display: 'inline-block',
            }} onClick={e => e.preventDefault()}>
              {ctaLabel}
            </a>
          </div>
        )}
      </div>
      {/* footer */}
      <div style={{ padding: '16px 32px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>
        © {new Date().getFullYear()} {company} · Powered by BrokerConnect
      </div>
    </div>
  );
}

/* ─── single template card ──────────────────────────────── */
function TemplateCard({ template, branding, isCompany, onSaved }) {
  const [open,      setOpen]      = useState(false);
  const [tab,       setTab]       = useState('edit');   // 'edit' | 'preview'
  const [subject,   setSubject]   = useState(template.subject   || '');
  const [bodyIntro, setBodyIntro] = useState(template.body_intro || '');
  const [bodyExtra, setBodyExtra] = useState(template.body_extra || '');
  const [ctaLabel,  setCtaLabel]  = useState(template.cta_label  || '');
  const [active,    setActive]    = useState(template.is_active !== false);
  const [saving,    setSaving]    = useState(false);
  const [resetting, setResetting] = useState(false);
  const [msg,       setMsg]       = useState(null); // { type, text }

  const bodyIntroRef = useRef(null);
  const bodyExtraRef = useRef(null);

  const isDirty = subject !== template.subject ||
    bodyIntro !== (template.body_intro || '') ||
    bodyExtra !== (template.body_extra || '') ||
    ctaLabel  !== (template.cta_label  || '') ||
    active    !== (template.is_active !== false);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/notification-templates/${template.template_key}`, {
        subject, body_intro: bodyIntro, body_extra: bodyExtra || null, cta_label: ctaLabel || null, is_active: active,
      });
      flash('success', 'Saved');
      onSaved?.();
    } catch (e) {
      flash('error', e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset this template to its default content?')) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/notification-templates/${template.template_key}/reset`);
      setSubject(data.subject || '');
      setBodyIntro(data.body_intro || '');
      setBodyExtra(data.body_extra || '');
      setCtaLabel(data.cta_label || '');
      setActive(data.is_active !== false);
      flash('success', 'Reset to default');
      onSaved?.();
    } catch (e) {
      flash('error', e.response?.data?.error || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  const insertTag = (tag, field) => {
    if (field === 'bodyIntro') insertAtCursor(bodyIntroRef, `{{${tag}}}`, bodyIntro, setBodyIntro);
    else if (field === 'bodyExtra') insertAtCursor(bodyExtraRef, `{{${tag}}}`, bodyExtra, setBodyExtra);
    else setSubject(v => v + `{{${tag}}}`);
  };

  return (
    <div style={{
      border: `1px solid ${open ? '#6366f1' : '#e2e8f0'}`,
      borderRadius: 10, marginBottom: 10, overflow: 'hidden',
      transition: 'border-color .15s',
    }}>
      {/* card header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
          cursor: 'pointer', background: '#fff',
          userSelect: 'none',
        }}
        onClick={() => setOpen(o => !o)}
      >
        {/* active toggle — stop propagation so click doesn't expand card */}
        <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox" checked={active}
              onChange={async e => {
                setActive(e.target.checked);
                try {
                  await api.patch(`/admin/notification-templates/${template.template_key}`, { is_active: e.target.checked });
                  onSaved?.();
                } catch { setActive(!e.target.checked); }
              }}
              style={{ display: 'none' }}
            />
            <div style={{
              width: 36, height: 20, borderRadius: 10,
              background: active ? '#10b981' : '#cbd5e1',
              position: 'relative', transition: 'background .2s',
            }}>
              <div style={{
                position: 'absolute', top: 2, left: active ? 18 : 2,
                width: 16, height: 16, borderRadius: '50%', background: '#fff',
                transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
              }} />
            </div>
          </label>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{template.name}</span>
            {recipientBadge(template.recipient_type)}
            {!active && <span style={{ fontSize: 10, fontWeight: 700, background: '#f1f5f9', color: '#94a3b8', borderRadius: 20, padding: '2px 8px' }}>Disabled</span>}
          </div>
          {template.trigger_description && (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>⚡ {template.trigger_description}</div>
          )}
        </div>

        <svg style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s', color: '#94a3b8' }}
          width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>

      {/* expanded editor */}
      {open && (
        <div style={{ borderTop: '1px solid #f1f5f9' }}>
          {/* tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
            {['edit', 'preview'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: 'none', borderBottom: tab === t ? '2px solid #6366f1' : '2px solid transparent',
                color: tab === t ? '#6366f1' : '#64748b',
              }}>
                {t === 'edit' ? '✏️ Edit' : '👁 Preview'}
              </button>
            ))}
          </div>

          {tab === 'preview' ? (
            <div style={{ padding: 24, background: '#f8fafc' }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                <strong>Subject:</strong>{' '}
                <span dangerouslySetInnerHTML={{
                  __html: subject.replace(/\{\{[\w_]+\}\}/g, m =>
                    `<span style="background:#fef9c3;color:#92400e;padding:0 3px;border-radius:3px">${m}</span>`)
                }} />
              </div>
              <EmailPreview branding={branding} subject={subject} bodyIntro={bodyIntro} bodyExtra={bodyExtra} ctaLabel={ctaLabel} />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 0 }}>
              {/* editor fields */}
              <div style={{ flex: 1, padding: '20px 20px 20px 20px' }}>
                {/* subject */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    Subject line
                  </label>
                  <input
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '9px 12px',
                      border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14,
                      color: '#1e293b', outline: 'none',
                    }}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                    placeholder="Email subject…"
                  />
                </div>

                {/* body intro */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    Opening paragraph <span style={{ color: '#94a3b8', fontWeight: 400 }}>(HTML allowed)</span>
                  </label>
                  <textarea
                    ref={bodyIntroRef}
                    value={bodyIntro}
                    onChange={e => setBodyIntro(e.target.value)}
                    rows={4}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '9px 12px',
                      border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13,
                      color: '#1e293b', outline: 'none', resize: 'vertical', lineHeight: 1.6,
                      fontFamily: 'monospace',
                    }}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                {/* body extra */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    Extra paragraph <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <textarea
                    ref={bodyExtraRef}
                    value={bodyExtra}
                    onChange={e => setBodyExtra(e.target.value)}
                    rows={3}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '9px 12px',
                      border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13,
                      color: '#1e293b', outline: 'none', resize: 'vertical', lineHeight: 1.6,
                      fontFamily: 'monospace',
                    }}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                  />
                </div>

                {/* cta label */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                    Button label <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    value={ctaLabel}
                    onChange={e => setCtaLabel(e.target.value)}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '9px 12px',
                      border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 14,
                      color: '#1e293b', outline: 'none',
                    }}
                    onFocus={e => e.target.style.borderColor = '#6366f1'}
                    onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                    placeholder="e.g. View Lead →"
                  />
                </div>

                {/* messages + actions */}
                {msg && (
                  <div style={{
                    padding: '8px 14px', borderRadius: 7, marginBottom: 12, fontSize: 13, fontWeight: 600,
                    background: msg.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: msg.type === 'success' ? '#065f46' : '#991b1b',
                  }}>
                    {msg.text}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    onClick={save}
                    disabled={saving || !isDirty}
                    style={{
                      padding: '9px 22px', background: isDirty ? '#6366f1' : '#e2e8f0',
                      color: isDirty ? '#fff' : '#94a3b8', border: 'none', borderRadius: 7,
                      fontWeight: 700, fontSize: 13, cursor: isDirty ? 'pointer' : 'default',
                    }}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    onClick={reset}
                    disabled={resetting}
                    style={{
                      padding: '9px 18px', background: 'none', color: '#64748b',
                      border: '1.5px solid #e2e8f0', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    {resetting ? 'Resetting…' : 'Reset to default'}
                  </button>
                </div>
              </div>

              {/* tag panel */}
              {template.available_tags?.length > 0 && (
                <div style={{
                  width: 200, flexShrink: 0, borderLeft: '1px solid #f1f5f9',
                  padding: '20px 14px', background: '#fafbfc',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                    Dynamic tags
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
                    Click to insert into body.
                  </div>
                  {template.available_tags.map(tag => (
                    <div key={tag} style={{ marginBottom: 4, display: 'flex', gap: 4 }}>
                      <button
                        onClick={() => insertTag(tag, 'bodyIntro')}
                        title="Insert into opening paragraph"
                        style={{
                          flex: 1, padding: '5px 8px', background: '#ede9fe', color: '#5b21b6',
                          border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          textAlign: 'left', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {`{{${tag}}}`}
                      </button>
                    </div>
                  ))}
                  <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 10, lineHeight: 1.5 }}>
                    Right-click a field and paste to insert anywhere.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── main page ─────────────────────────────────────────── */
export default function EmailTemplatesPage() {
  const [templates,  setTemplates]  = useState([]);
  const [branding,   setBranding]   = useState(null);
  const [isCompany,  setIsCompany]  = useState(false);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [activeTab,  setActiveTab]  = useState('notification'); // 'notification' | 'stage' | 'backbook'
  const [category,   setCategory]   = useState('all');
  const [search,     setSearch]     = useState('');
  // Which modules this company has on (14 Aug 2026) — same pattern as Sidebar/Settings,
  // untouched for a normal all-modules-on account.
  const [navModules, setNavModules] = useState({
    leads: true, deals: true, introducers: true, backbook: false,
    clients: true, analytics: true, announcements: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tmplRes, settingsRes] = await Promise.allSettled([
        api.get('/admin/notification-templates'),
        api.get('/admin/company-profile/settings'),
      ]);
      if (tmplRes.status === 'fulfilled') setTemplates(tmplRes.value.data);
      if (settingsRes.status === 'fulfilled') {
        const d = settingsRes.value.data;
        setBranding(d.company?.branding || {});
        setIsCompany(d.company?.plan === 'company' || (d.company?.team_members?.length > 0));
      }
    } catch (e) {
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/company-profile/nav-modules')
      .then(r => setNavModules(prev => ({ ...prev, ...r.data?.nav_modules })))
      .catch(() => {});
  }, []);

  // Templates visible to this user (respects tier)
  const tierFiltered = templates.filter(t => isCompany || t.tier !== 'company');

  const filtered = tierFiltered.filter(t => {
    if (category !== 'all' && t.category !== category) return false;
    if (search) {
      const q = search.toLowerCase();
      return t.name.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q) || t.template_key.includes(q);
    }
    return true;
  });

  // Only show team/partners filters when user has team members
  const CATEGORY_MODULE = { leads: 'leads', deals: 'deals', commission: 'deals', clients: 'clients', introducers: 'introducers' };
  const visibleCategories = CATEGORIES.filter(c => {
    if (!isCompany && (c.key === 'team' || c.key === 'partners')) return false;
    const requiredModule = CATEGORY_MODULE[c.key];
    if (requiredModule && navModules[requiredModule] === false) return false;
    return true;
  });

  const grouped = visibleCategories.filter(c => c.key !== 'all').reduce((acc, c) => {
    const list = filtered.filter(t => t.category === c.key);
    if (list.length) acc.push({ ...c, templates: list });
    return acc;
  }, []);

  const totalActive = tierFiltered.filter(t => t.is_active).length;

  const pageContent = (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px', fontFamily: 'system-ui, sans-serif' }}>

      {/* page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', margin: 0 }}>Email Templates</h1>
        <p style={{ color: '#64748b', fontSize: 14, margin: '6px 0 0' }}>
          Customise every automated email sent by your BrokerConnect account. Changes apply immediately.
        </p>
      </div>

      {/* branding strip */}
      {branding && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px',
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 24,
        }}>
          {branding.logo_url
            ? <img src={branding.logo_url} alt="logo" style={{ height: 36, objectFit: 'contain', borderRadius: 4 }} />
            : (
              <div style={{
                width: 36, height: 36, borderRadius: '50%', background: branding.primary_color || '#1e293b',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800,
              }}>
                {(branding.company_name || '?')[0]}
              </div>
            )
          }
          <div>
            <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{branding.company_name || 'Your Company'}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>
              Emails pull this branding automatically · {totalActive} of {tierFiltered.length} templates active
            </div>
          </div>
          <a href="/admin/settings#branding" style={{ marginLeft: 'auto', fontSize: 12, color: '#6366f1', fontWeight: 600, textDecoration: 'none' }}>
            Edit branding →
          </a>
        </div>
      )}

      {/* top tabs */}
      <div style={{ display: 'flex', gap: 2, background: '#f1f5f9', borderRadius: 8, padding: 4, marginBottom: 24, width: 'fit-content' }}>
        {[
          { key: 'notification', label: '🔔 System notifications' },
          ...(navModules.deals !== false ? [{ key: 'stage', label: '🏠 Deal stage emails' }] : []),
          ...(navModules.backbook ? [{ key: 'backbook', label: '📖 Back Book emails' }] : []),
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '8px 18px', border: 'none', borderRadius: 6, cursor: 'pointer',
            fontWeight: 600, fontSize: 13,
            background: activeTab === t.key ? '#fff' : 'none',
            color: activeTab === t.key ? '#1e293b' : '#64748b',
            boxShadow: activeTab === t.key ? '0 1px 4px rgba(0,0,0,.08)' : 'none',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
          {error}
        </div>
      )}

      {activeTab === 'notification' && (
        <>
          {/* search + category filter */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <input
              placeholder="Search templates…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                flex: 1, minWidth: 200, padding: '9px 14px', border: '1.5px solid #e2e8f0',
                borderRadius: 8, fontSize: 14, outline: 'none', color: '#1e293b',
              }}
              onFocus={e => e.target.style.borderColor = '#6366f1'}
              onBlur={e => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
            {visibleCategories.map(c => {
              const count = c.key === 'all'
                ? tierFiltered.length
                : tierFiltered.filter(t => t.category === c.key).length;
              return (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  style={{
                    padding: '6px 14px', border: '1.5px solid', cursor: 'pointer', borderRadius: 20,
                    fontSize: 12, fontWeight: 600,
                    background: category === c.key ? '#6366f1' : '#fff',
                    borderColor: category === c.key ? '#6366f1' : '#e2e8f0',
                    color: category === c.key ? '#fff' : '#475569',
                  }}
                >
                  {c.icon} {c.label} <span style={{ opacity: .7 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>Loading templates…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8', fontSize: 14 }}>No templates match your filter.</div>
          ) : category !== 'all' ? (
            /* flat list for single category */
            filtered.map(t => (
              <TemplateCard key={t.template_key} template={t} branding={branding} isCompany={isCompany} onSaved={load} />
            ))
          ) : (
            /* grouped by category */
            grouped.map(g => (
              <div key={g.key} style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 18 }}>{g.icon}</span>
                  <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b' }}>{g.label}</h2>
                  <span style={{ fontSize: 12, color: '#94a3b8' }}>{g.templates.length} template{g.templates.length !== 1 ? 's' : ''}</span>
                </div>
                {g.templates.map(t => (
                  <TemplateCard key={t.template_key} template={t} branding={branding} isCompany={isCompany} onSaved={load} />
                ))}
              </div>
            ))
          )}

        </>
      )}

      {activeTab === 'stage' && (
        <div>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
            Deal stage emails are sent to introducers when their client's deal moves through your pipeline. Create templates here and attach them to stages in your pipeline settings.
          </p>
          <AdminEmailTemplatesEmbed excludeNamePrefixes={navModules.backbook ? ['Back Book Renewal Reminder'] : []} />
        </div>
      )}

      {activeTab === 'backbook' && (
        <div>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
            Sent automatically ahead of a product's end date, on the schedule set in Settings → Calendar &amp; Booking.
            Each trigger point (8/6/3/2/1 months by default) has its own template with copy that gets more
            urgent the closer the renewal gets — all fully editable, but system-managed, so none can be
            deleted or duplicated here.
          </p>
          <AdminEmailTemplatesEmbed restrictToNamePrefixes={['Back Book Renewal Reminder']} allowCreate={false} allowDelete={false} />
        </div>
      )}
    </div>
  );

  if (loading && !templates.length) {
    return (
      <div className="dashboard-container">
        <Sidebar />
        <main className="dashboard-main" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#6b7280' }}>Loading…</span>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        {pageContent}
      </main>
    </div>
  );
}

/* ─── embedded deal stage templates ─────────────────────── */
function AdminEmailTemplatesEmbed({ restrictToNames = null, excludeNames = [], restrictToNamePrefixes = null, excludeNamePrefixes = [], allowCreate = true, allowDelete = true } = {}) {
  const [templates, setTemplates] = useState([]);
  const [tokens,    setTokens]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [showForm,  setShowForm]  = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [form, setForm] = useState({ name: '', subject: '', body_html: '', is_active: true });
  const [showTest,   setShowTest]   = useState(false);
  const [testTo,     setTestTo]     = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testMsg,    setTestMsg]    = useState(null); // { type: 'success'|'error', text }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/email-templates');
      const scoped = data
        .filter(t => !restrictToNames || restrictToNames.includes(t.name))
        .filter(t => !excludeNames.includes(t.name))
        .filter(t => !restrictToNamePrefixes || restrictToNamePrefixes.some(p => t.name.startsWith(p)))
        .filter(t => !excludeNamePrefixes.some(p => t.name.startsWith(p)));
      setTemplates(scoped);
    } catch { setError('Failed to load'); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictToNames?.join(','), excludeNames.join(','), restrictToNamePrefixes?.join(','), excludeNamePrefixes.join(',')]);

  useEffect(() => {
    load();
    api.get('/admin/email-templates/tokens').then(r => setTokens(r.data)).catch(() => {});
  }, [load]);

  const flash = (setter, msg) => { setter(msg); setTimeout(() => setter(''), 4000); };

  const openEdit = t => {
    setEditId(t.id);
    setForm({ name: t.name||'', subject: t.subject||'', body_html: t.body_html||'', is_active: t.is_active !== false });
    setShowForm(true);
    setShowTest(false); setTestTo(''); setTestMsg(null);
  };

  const handleSendTest = async () => {
    const to = testTo.trim();
    if (!to) return;
    if (!form.subject.trim() || !form.body_html.trim()) {
      setTestMsg({ type: 'error', text: 'Add a subject and body first' });
      return;
    }
    setTestSending(true); setTestMsg(null);
    try {
      await api.post('/admin/email-templates/test-send', { to, subject: form.subject, body_html: form.body_html });
      setTestMsg({ type: 'success', text: `Test sent to ${to} — check that inbox.` });
    } catch (e) {
      setTestMsg({ type: 'error', text: e.response?.data?.error || 'Failed to send test email' });
    } finally {
      setTestSending(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body_html.trim()) return flash(setError, 'All fields required');
    setSaving(true);
    try {
      if (editId) { await api.put(`/admin/email-templates/${editId}`, form); flash(setSuccess, 'Updated'); }
      else { await api.post('/admin/email-templates', form); flash(setSuccess, 'Created'); }
      setShowForm(false); setEditId(null); setForm({ name:'', subject:'', body_html:'', is_active:true });
      setShowTest(false); setTestTo(''); setTestMsg(null);
      load();
    } catch (e) { flash(setError, e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async t => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try { await api.delete(`/admin/email-templates/${t.id}`); flash(setSuccess, 'Deleted'); load(); }
    catch (e) { flash(setError, e.response?.data?.error || 'Delete failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 14, color: '#64748b' }}>
          {(restrictToNames || restrictToNamePrefixes) ? `${templates.length} system template${templates.length !== 1 ? 's' : ''}` : `${templates.length} custom template${templates.length !== 1 ? 's' : ''}`}
        </div>
        {allowCreate && (
        <button onClick={() => { setEditId(null); setForm({ name:'', subject:'', body_html:'', is_active:true }); setShowForm(true); setShowTest(false); setTestTo(''); setTestMsg(null); }}
          style={{ padding: '8px 16px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          + New template
        </button>
        )}
      </div>

      {error   && <div style={{ background:'#fee2e2',color:'#991b1b',padding:'10px 14px',borderRadius:8,marginBottom:12,fontSize:13 }}>{error}</div>}
      {success && <div style={{ background:'#d1fae5',color:'#065f46',padding:'10px 14px',borderRadius:8,marginBottom:12,fontSize:13 }}>{success}</div>}

      {loading ? <div style={{ textAlign:'center',padding:40,color:'#94a3b8' }}>Loading…</div>
      : templates.length === 0
      ? <div style={{ textAlign:'center',padding:40,color:'#94a3b8',border:'2px dashed #e2e8f0',borderRadius:10,fontSize:14 }}>
          {(restrictToNames || restrictToNamePrefixes) ? 'Not found yet — it seeds itself the first time this is switched on.' : 'No custom templates yet. Create one to attach to a pipeline stage.'}
        </div>
      : templates.map(t => (
        <div key={t.id} style={{ display:'flex',alignItems:'center',gap:12,padding:'14px 16px',background:'#fff',border:'1px solid #e2e8f0',borderRadius:9,marginBottom:8 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontWeight:700,color:'#1e293b',fontSize:14 }}>{t.name}</div>
            <div style={{ fontSize:12,color:'#64748b',marginTop:2 }}>{t.subject}</div>
          </div>
          {!t.is_active && <span style={{ fontSize:11,fontWeight:700,background:'#f1f5f9',color:'#94a3b8',borderRadius:20,padding:'2px 8px' }}>Inactive</span>}
          <button onClick={() => openEdit(t)} style={{ padding:'6px 14px',border:'1.5px solid #e2e8f0',borderRadius:7,background:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',color:'#1e293b' }}>Edit</button>
          {allowDelete && (
          <button onClick={() => handleDelete(t)} style={{ padding:'6px 14px',border:'1.5px solid #fecaca',borderRadius:7,background:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',color:'#dc2626' }}>Delete</button>
          )}
        </div>
      ))}

      {showForm && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:24 }}
          onClick={() => setShowForm(false)}>
          <div style={{ background:'#fff',borderRadius:12,width:'100%',maxWidth:700,maxHeight:'90vh',overflow:'auto' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'20px 24px',borderBottom:'1px solid #e2e8f0',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
              <h3 style={{ margin:0,fontSize:16,fontWeight:700,color:'#1e293b' }}>{editId ? 'Edit template' : 'New deal stage template'}</h3>
              <button onClick={() => setShowForm(false)} style={{ background:'none',border:'none',fontSize:22,cursor:'pointer',color:'#94a3b8' }}>×</button>
            </div>
            <div style={{ padding:24, display:'flex', gap:20 }}>
              <div style={{ flex:1 }}>
                {[
                  { label:'Template name', key:'name', placeholder:'e.g. DIP ready for introducer' },
                  { label:'Subject line', key:'subject', placeholder:'e.g. Update on {{client_name}}' },
                ].map(f => (
                  <div key={f.key} style={{ marginBottom:16 }}>
                    <label style={{ display:'block',fontSize:12,fontWeight:700,color:'#374151',marginBottom:6 }}>{f.label}</label>
                    <input value={form[f.key]} onChange={e => setForm(fm => ({...fm,[f.key]:e.target.value}))} placeholder={f.placeholder}
                      style={{ width:'100%',boxSizing:'border-box',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:7,fontSize:14,outline:'none',color:'#1e293b' }} />
                  </div>
                ))}
                <div style={{ marginBottom:16 }}>
                  <label style={{ display:'block',fontSize:12,fontWeight:700,color:'#374151',marginBottom:6 }}>Body (HTML allowed)</label>
                  <textarea value={form.body_html} onChange={e => setForm(fm => ({...fm,body_html:e.target.value}))} rows={10}
                    style={{ width:'100%',boxSizing:'border-box',padding:'9px 12px',border:'1.5px solid #e2e8f0',borderRadius:7,fontSize:13,outline:'none',resize:'vertical',fontFamily:'monospace',lineHeight:1.6 }} />
                </div>
                <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:14 }}>
                  <input type="checkbox" checked={form.is_active} onChange={e => setForm(fm => ({...fm,is_active:e.target.checked}))} />
                  Active
                </label>

                <div style={{ marginTop:18, paddingTop:16, borderTop:'1px solid #f1f5f9' }}>
                  <button type="button" onClick={() => setShowTest(s => !s)}
                    style={{ padding:'7px 14px',border:'1.5px solid #e2e8f0',borderRadius:7,background:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',color:'#1e293b' }}>
                    ✉️ Send test email
                  </button>
                  {showTest && (
                    <div style={{ display:'flex',gap:8,marginTop:10,alignItems:'center' }}>
                      <input type="email" value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@example.com"
                        onKeyDown={e => { if (e.key === 'Enter' && testTo.trim() && !testSending) handleSendTest(); }}
                        style={{ flex:1,boxSizing:'border-box',padding:'8px 12px',border:'1.5px solid #e2e8f0',borderRadius:7,fontSize:13,outline:'none',color:'#1e293b' }} />
                      <button type="button" onClick={handleSendTest} disabled={testSending || !testTo.trim()}
                        style={{ padding:'8px 16px',background:'#10b981',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:13,cursor: testSending || !testTo.trim() ? 'default' : 'pointer',opacity: testSending || !testTo.trim() ? 0.6 : 1,whiteSpace:'nowrap' }}>
                        {testSending ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  )}
                  {testMsg && (
                    <div style={{ marginTop:8,fontSize:12,fontWeight:600,color: testMsg.type === 'error' ? '#dc2626' : '#059669' }}>
                      {testMsg.text}
                    </div>
                  )}
                  <p style={{ margin:'8px 0 0',fontSize:11,color:'#94a3b8',lineHeight:1.5 }}>
                    Sends the subject/body above (with sample data filling in any tokens) through the real email pipeline — no need to save first.
                  </p>
                </div>
              </div>
              {tokens.length > 0 && (
                <div style={{ width:160,flexShrink:0,borderLeft:'1px solid #f1f5f9',paddingLeft:16 }}>
                  <div style={{ fontSize:11,fontWeight:700,color:'#94a3b8',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:10 }}>Tokens</div>
                  {tokens.map(t => (
                    <button key={t.token} onClick={() => setForm(fm => ({...fm,body_html:(fm.body_html||'')+t.token}))}
                      title={t.description}
                      style={{ display:'block',width:'100%',textAlign:'left',padding:'4px 8px',marginBottom:4,background:'#ede9fe',color:'#5b21b6',border:'none',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>
                      {t.token}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ padding:'16px 24px',borderTop:'1px solid #e2e8f0',display:'flex',gap:10,justifyContent:'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={{ padding:'9px 18px',border:'1.5px solid #e2e8f0',borderRadius:7,background:'#fff',fontSize:13,fontWeight:600,cursor:'pointer' }}>Cancel</button>
              <button onClick={handleSubmit} disabled={saving} style={{ padding:'9px 22px',background:'#6366f1',color:'#fff',border:'none',borderRadius:7,fontWeight:700,fontSize:13,cursor:'pointer' }}>
                {saving ? 'Saving…' : editId ? 'Save changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
